import React, { Fragment, useEffect, useState, useRef } from "react";
import moment, { Moment } from "moment";
import * as _ from "lodash";
import ReactGA4 from "react-ga4";
import { useSelector, useDispatch } from "react-redux";
import * as WorkerTimers from "worker-timers";
import { useOktaAuth, LoginCallback, SecureRoute } from "@okta/okta-react";
import {
  Redirect,
  Route,
  Switch,
  useHistory,
  useLocation,
} from "react-router-dom";
import {
  DashboardComponent,
  AdminComponent,
  HistoryComponent,
  ToolsPageComponent,
  MappingAdminComponent,
  ToolTypeComponent,
  MappingManagerComponent,
  MappingMapperComponent,
  MappingReviewComponent,
  UserProfileComponent,
  RequiredInfoComponent
} from "./config";
import { CircularProgress } from "../../UI/Progress";
import LoginComponent from "../Login";
import { SessionTimeoutAlert } from "../SessionTimeout";
import { getInitialData } from "../../store/actions/Initial";
import { AccessComponent } from "../../UI/Access";
import { getEmailMessageForPortalError } from "../Utility";
import { TiktokCallback } from "./TiktokCallback";
import { QueryGenerator } from "../../components/Admin/QueryGenerator";
import TiktokPoupHandler from "./TiktokPoupHandler";
import { GoogleCallBack } from "./GoogleCallBack";
import MSLoginCallBack from "./MSLoginCallBack";
import { MetaResp } from "./MetaResp";
import { SnapChatCallBack } from "./SnapChatCallBack";

export const AppRoutes = (): React.JSX.Element => {
  const dispatch = useDispatch();
  const { authState, oktaAuth } = useOktaAuth();
  const [isTokenExpired, setTokenExpired] = useState(true);
  const [currentUrl, setCurrentUrl] = useState("");

  const [showDialog, setShowDialog] = useState<boolean>(false);
  const [timer, setTimer] = useState<number | null>(null);
  const sessionTimer: any = useRef();
  const history = useHistory();
  const location = useLocation();

  const initial = useSelector((state: any) => state.initial);

  const extendUserSession = (): Promise<any> => {
    if (!_.isUndefined(sessionTimer.current)) {
      WorkerTimers.clearTimeout(sessionTimer.current);
    }

    return new Promise((resolve: any, reject: any) => {
      oktaAuth.token
        .getWithoutPrompt()
        .then(async (res: any) => {
          setShowDialog(false);
          oktaAuth.tokenManager.setTokens(res.tokens);
          oktaAuth.authStateManager.updateAuthState();
          await startSessionTimer(res.tokens);
          resolve();
        })
        .catch((err: any) => {
          reject(err);
          console.log(err);
        });
    });
  };

  const getTokenExpiryTime = async (tokens: any): Promise<any> => {
    if (
      !authState?.isAuthenticated ||
      _.isEmpty(tokens) ||
      _.isUndefined(tokens.idToken)
    ) {
      return null;
    }

    const ExpiresAt: Moment = moment.unix(tokens.idToken.expiresAt);
    const CurrentTS: number = moment().unix();
    return ExpiresAt.diff(moment.unix(CurrentTS));
  };

  const validateToken = async (callback?: any): Promise<void> => {
    const tokens: any = await oktaAuth.tokenManager.getTokens();
    const TokenExpiryTime = await getTokenExpiryTime(tokens);

    if (_.isNull(TokenExpiryTime)) {
      return;
    }

    try {
      await extendUserSession();
    } catch (err: any) {
      console.log(authState?.isAuthenticated);
      if (!authState?.isAuthenticated) {
        setTokenExpired(true);
        setShowDialog(true);
      }
      console.log(err);
    }
    callback();
  };

  const startSessionTimer = async (tokens: any): Promise<void> => {
    const TokenExpiryTime = await getTokenExpiryTime(tokens);
    let tokenExpirySeconds = 0;
    if (!_.isNull(TokenExpiryTime)) {
      tokenExpirySeconds = moment.duration(TokenExpiryTime).asSeconds();
      tokenExpirySeconds = tokenExpirySeconds - 600;
    }

    sessionTimer.current = WorkerTimers.setTimeout(async () => {
      window.onbeforeunload = null;
      setShowDialog(true);
      WorkerTimers.clearTimeout(sessionTimer.current);
    }, tokenExpirySeconds * 1000);
  };

  // Clear session internal and timeout
  const cleanupAcitvity = (): void => {
    if (!_.isUndefined(sessionTimer.current)) {
      WorkerTimers.clearTimeout(sessionTimer.current);
    }

    if (!_.isNull(timer)) {
      WorkerTimers.clearTimeout(timer);
    }
  };

  useEffect(() => {
    setCurrentUrl(window.location.href);
  }, []);

  useEffect(() => {
    const { pathname, search } = location;
    const isExtendpresentInURL = currentUrl.includes("?embed=true");
    const regex = /embed=true/g;
    const matches = search.match(regex);
    const count = matches ? matches.length : 0;

    if (isExtendpresentInURL && !_.gt(count, 0)) {
      const newSearch = search ? `${search}&embed=true` : "?embed=true";
      const newLocation = `${pathname}${newSearch}`;
      window.history.replaceState({}, "", newLocation);
    }
    console.log("replaceState",window.location.href);
  }, [location]);

  useEffect(() => {
    const pathname = location.pathname;
    const isMatch = _.some(["tt","tiktokhandler","callback","meta"],path=>_.includes(pathname,path));
    if(!isMatch){
      localStorage.removeItem("traceid");
    }
  }, [location.pathname]);

  useEffect(() => {
    if (authState?.isAuthenticated) {
      setTokenExpired(false);
      validateToken(() => {
        dispatch(getInitialData());
      });

      const timeout: any = WorkerTimers.setTimeout(async () => {
        WorkerTimers.clearTimeout(sessionTimer.current);
        const tokens: any = await oktaAuth.tokenManager.getTokens();
        startSessionTimer(tokens);
      }, 5 * 60 * 1000);
      setTimer(timeout);

      if (!_.isNull(localStorage.getItem("redirectUrl"))) {
        history.push(`/tool${localStorage.getItem("redirectUrl")}`);
        localStorage.removeItem("redirectUrl");
      }
    } else {
      setTokenExpired(true);
      cleanupAcitvity();
    }

    return () => cleanupAcitvity();
  }, [authState?.isAuthenticated]);

  const onLogoutRedirect =async (): Promise<void> => {
    if(initial.isEmbed){
      window.location.href = "/?embed=true";
    }else{
      window.location.href = "/";
    }
  };


  useEffect(() => {
    if (authState?.isAuthenticated && !initial.loading && !initial.error) {
      const GA_MEASUREMENT_ID: any = process.env.REACT_APP_GA_MEASUREMENT_ID;

      ReactGA4.initialize(GA_MEASUREMENT_ID, {
        gaOptions: {
          userId: initial.userInfo.userId,
        },
      });
      history.listen((loca: any) => {
        ReactGA4.send({
          hitType: "pageview",
          page: loca.pathname + loca.search,
        });
      });
    }
  }, [initial.loading, authState?.isAuthenticated]);

  if (authState?.isAuthenticated && initial.loading) {
    return <CircularProgress />;
  }

  if (!initial.isAuthenticated && !initial.loading) {
    if (!initial.error) {
      return (
        <AccessComponent
          heading={"Unauthorized Access."}
          description={`You are not authorized to use Automations Portal, please contact the support at <a href="${getEmailMessageForPortalError(
            "Unauthorized Access.",
            " error"
          )}">gdt-automations-support@groupm.tech</a>.`}
        />
      );
    }
    return (
      <AccessComponent
        heading={"Server Error."}
        description={`Please refresh or try again later. If the error persists, please contact the support at <a href="${getEmailMessageForPortalError(
          "Server Error."
        )}">gdt-automations-support@groupm.tech</a>.`}
      />
    );
  }

  return (
    <Fragment>
      <Switch>
        <Route
          path="/login/callback"
          render={(props: any) => {
            return <LoginCallback {...props} />;
          }}
        />
        <Route path="/login" component={LoginComponent} />
        <Route path="/tt/callback" component={TiktokCallback} />
        <Route path="/tiktokhandler" component={TiktokPoupHandler} />
        <Route path="/snapchathandler" component={SnapChatCallBack} />
        <Route path="/google/callback" component={GoogleCallBack} />
        <Route path="/meta" component={MetaResp} />
        <Route path="/web/microsoft/callback" component={MSLoginCallBack} />
        <SecureRoute path="/profile" exact component={UserProfileComponent} />
        <SecureRoute path="/" exact component={ToolsPageComponent} />
        <SecureRoute path="/dashboard" exact component={DashboardComponent} />
        <SecureRoute
          path="/admin/tools-configuration"
          exact
          component={AdminComponent}
        />
        <SecureRoute
          path="/admin/query-builder"
          exact
          component={QueryGenerator}
        />
        <SecureRoute
          path="/required-info"
          exact
          component={RequiredInfoComponent}
        />
        <SecureRoute path="/tool" exact component={ToolTypeComponent} />
        <SecureRoute
          path="/mapping/admin"
          exact
          component={MappingAdminComponent}
        />
        <SecureRoute
          path="/mapping/manager"
          exact
          component={MappingManagerComponent}
        />
        <SecureRoute
          path="/mapping/mapper"
          exact
          component={MappingMapperComponent}
        />
        <SecureRoute
          path="/mapping/review"
          exact
          component={MappingReviewComponent}
        />
        <SecureRoute path={"/history"} exact component={HistoryComponent} />
        <Route
          render={() => {
            return <Redirect to={"/"} />;
          }}
        />
      </Switch>

      {showDialog && (
        <SessionTimeoutAlert
          onContinueSignedIn={() => {
            setShowDialog(false);
            extendUserSession();
          }}
          timeoutSeconds={300}
          onLogout={() => {
            setShowDialog(false);
            if (oktaAuth && authState?.isAuthenticated) {
              oktaAuth.signOut().catch(() => {
                onLogoutRedirect();
              });
              return;
            }
            onLogoutRedirect();
          }}
          closeAlert={() => {
            setShowDialog(false);
          }}
          isTokenExpired={isTokenExpired}
        />
      )}
    </Fragment>
  );
};
